import React from 'react';
import { Card, Row, Col, Tag, Typography, Space, Button } from 'antd';
import { GoogleOutlined, FacebookOutlined, BarChartOutlined, DatabaseOutlined, EyeOutlined, CloseOutlined, DeleteOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const AuditCard = ({ audit, onViewDetails, getStatusColor, getStatusText, onCancelAudit, onDeleteAudit, onToggleVisibility }) => {
  const getAuditIcon = (auditType) => {
    if (auditType === 'google_ads') {
      return <GoogleOutlined style={{ fontSize: 24, color: '#4285F4' }} />;
    }
    if (auditType === 'google_analytics') {
      return <BarChartOutlined style={{ fontSize: 24, color: '#FF9800' }} />;
    }
    if (auditType === 'custom_data') {
      return <DatabaseOutlined style={{ fontSize: 24, color: '#722ED1' }} />;
    }
    return <FacebookOutlined style={{ fontSize: 24, color: '#1877F2' }} />;
  };

  const getAuditTypeName = (auditType) => {
    if (auditType === 'google_ads') return 'Google Ads';
    if (auditType === 'facebook' || auditType === 'facebook_ads') return 'Facebook Ads';
    if (auditType === 'google_analytics') return 'Google Analytics';
    if (auditType === 'custom_data') return 'ReportsUp';
    return auditType || 'Bilinmiyor';
  };

  return (
    <Card
      size="small"
      style={{
        borderLeft: `4px solid ${getStatusColor(audit.status)}`,
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      onClick={() => onViewDetails(audit)}
      hoverable
      actions={[
        <Space key="view">
          <EyeOutlined />
          <Text>Detayları Gör</Text>
        </Space>,
        ...(audit.status === 'pending' && onCancelAudit ? [
          <Button 
            key="cancel"
            type="text" 
            size="small"
            danger
            icon={<CloseOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onCancelAudit(audit);
            }}
          >
            İptal Et
          </Button>
        ] : []),
        ...(onToggleVisibility ? [
          <Button 
            key="visibility"
            type="text" 
            size="small"
            icon={audit.hidden_from_brand || audit.hiddenFromBrand ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility(audit);
            }}
          >
            {audit.hidden_from_brand || audit.hiddenFromBrand ? 'Markaya Göster' : 'Markadan Gizle'}
          </Button>
        ] : []),
        ...(onDeleteAudit ? [
          <Button 
            key="delete"
            type="text" 
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteAudit(audit);
            }}
          >
            Sil
          </Button>
        ] : [])
      ]}
    >
      <Row align="middle" gutter={16}>
        {/* Icon */}
        <Col span={2}>
          {getAuditIcon(audit.auditType || audit.audit_type)}
        </Col>

        {/* Brand & Account */}
        <Col span={6}>
          <Text strong style={{ fontSize: '16px' }}>
            {audit.Brand?.name || 'Bilinmiyor'}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {getAuditTypeName(audit.auditType || audit.audit_type)}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {audit.account_id}
          </Text>
        </Col>

        {/* Prompt Preview */}
        <Col span={8}>
          <Text
            ellipsis={{ rows: 2 }}
            style={{ fontSize: '13px' }}
          >
            {audit.prompt}
          </Text>
        </Col>

        {/* Status */}
        <Col span={3}>
          <Tag 
            color={getStatusColor(audit.status)}
            style={{ 
              borderRadius: '12px',
              fontWeight: 'bold'
            }}
          >
            {getStatusText(audit.status)}
          </Tag>
        </Col>

        {/* Date & Time */}
        <Col span={3}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {audit.created_at ? dayjs(audit.created_at).format('DD.MM.YYYY') : 'Tarih yok'}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {audit.created_at ? dayjs(audit.created_at).format('HH:mm') : '--:--'}
          </Text>
          {process.env.NODE_ENV === 'development' && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: '9px', color: '#999' }}>
                Raw: {audit.created_at}
              </Text>
            </>
          )}
        </Col>

        {/* Response Time & Model */}
        <Col span={2}>
          {audit.response_time && (
            <>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {audit.response_time}
              </Text>
              <br />
            </>
          )}
          {audit.model && (
            <Text type="secondary" style={{ fontSize: '10px' }}>
              {audit.model.split('/').pop()}
            </Text>
          )}
        </Col>
      </Row>

      {/* Error Preview */}
      {audit.status === 'error' && audit.error_message && (
        <Row style={{ marginTop: 8 }}>
          <Col span={24}>
            <Text 
              type="danger" 
              style={{ fontSize: '12px' }}
              ellipsis
            >
              {audit.error_message}
            </Text>
          </Col>
        </Row>
      )}

      {/* Success Preview */}
      {audit.status === 'success' && audit.analysis && (
        <Row style={{ marginTop: 8 }}>
          <Col span={24}>
            <Text 
              type="secondary" 
              style={{ fontSize: '12px' }}
              ellipsis={{ rows: 1 }}
            >
              {audit.analysis.substring(0, 150)}...
            </Text>
          </Col>
        </Row>
      )}
    </Card>
  );
};

export default AuditCard;